'use client';
import Image from 'next/image';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { cn } from 'lib/utils';
import { type BloomProps, Bloom, HeaderSection, Wrapper } from 'ui';

export type LogoGridProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  logos: {
    src: string;
    alt: string;
    width: number;
    height: number;
  }[];
  bloom?: BloomProps;
};

const LogoGrid = ({
  className,
  eyebrow,
  title,
  intro,
  logos,
  bloom,
}: LogoGridProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  return (
    <motion.section {...{ ref }}>
      <Wrapper className={cn('relative flex flex-col gap-14', className)}>
        <Bloom {...bloom} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <HeaderSection
            className="relative z-2"
            content={intro}
            alignment="center"
            eyebrowAsTitle={!!eyebrow}
            {...{
              eyebrow,
              title,
            }}
          />
        </motion.div>

        <motion.ul
          className="relative z-2 flex justify-center flex-wrap gap-x-4 md:gap-x-8"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          {logos.map((logo, index) => (
            <motion.li
              className="flex items-center justify-center shrink-0 grow-0 h-33 md:h-41 px-8"
              key={logo.src}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{
                duration: 0.5,
                delay: 0.4 + index * 0.1,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <Image
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
                className="shrink-0 max-w-23 md:max-w-30 h-auto object-contain"
              />
            </motion.li>
          ))}
        </motion.ul>
      </Wrapper>
    </motion.section>
  );
};

export default LogoGrid;
